import React, { useEffect, useState } from 'react'
import { leaV3API, PlanListRow, PlanDetail } from '../api/leaV3.api'

export const PlansView: React.FC = () => {
  const [rows, setRows] = useState<PlanListRow[]>([])
  const [loading, setLoading] = useState(true)
  const [openId, setOpenId] = useState<string | null>(null)
  const [detail, setDetail] = useState<PlanDetail | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [showCancelled, setShowCancelled] = useState(false)

  useEffect(() => {
    setLoading(true)
    leaV3API.getPlans({ limit: 200, includeCancelled: showCancelled })
      .then((r) => setRows(r.data))
      .finally(() => setLoading(false))
  }, [showCancelled])

  const open = async (id: string) => {
    if (openId === id) { setOpenId(null); setDetail(null); return }
    setOpenId(id); setLoadingDetail(true); setDetail(null)
    try {
      const r = await leaV3API.getPlanDetail(id)
      setDetail(r.data)
    } finally {
      setLoadingDetail(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 text-blue-900 px-4 py-3 rounded">
        <div className="font-semibold mb-1">Plans proposés par Léa V3</div>
        <div className="text-sm">
          <strong>Strict dry-run :</strong> ces plans ont été créés par le scheduler en appelant Claude. Aucune action
          n'a été exécutée (pas d'email envoyé, pas de SMS, pas de tracker_com). Seules les tables{' '}
          <code className="bg-blue-100 px-1 rounded">lea_v3.*</code> ont été modifiées. Les plans marqués{' '}
          <span className="px-1 bg-yellow-100 text-yellow-900 rounded text-xs">MOCK</span> ont été générés sans clé Claude.
        </div>
      </div>

      {loading ? (
        <div className="p-4 text-gray-500">Chargement…</div>
      ) : rows.length === 0 ? (
        <div className="p-4 text-gray-500">Aucun plan. Lance <code>scheduler.py --max-plans 2</code> pour en créer.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((p) => (
            <PlanRow
              key={p.id}
              plan={p}
              expanded={openId === p.id}
              onToggle={() => open(p.id)}
              detail={openId === p.id ? detail : null}
              loadingDetail={openId === p.id && loadingDetail}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const PlanRow: React.FC<{
  plan: PlanListRow
  expanded: boolean
  onToggle: () => void
  detail: PlanDetail | null
  loadingDetail: boolean
}> = ({ plan, expanded, onToggle, detail, loadingDetail }) => {
  return (
    <article className="bg-white border border-gray-200 rounded">
      <button
        onClick={onToggle}
        className="w-full px-3 py-2 flex items-center gap-3 text-left hover:bg-gray-50"
      >
        <span className="text-gray-400 text-xs">{expanded ? '▼' : '▶'}</span>
        <span className="font-mono font-semibold text-sm">#{plan.affaire_id}</span>
        <span className="text-xs px-2 py-0.5 bg-gray-100 rounded font-mono">{plan.matched_route_ids[0]}</span>
        {plan.ladder_slot && (
          <span className="text-xs px-2 py-0.5 bg-amber-50 border border-amber-200 rounded font-mono text-amber-900">{plan.ladder_slot}</span>
        )}
        <span className="text-xs text-gray-600">{plan.category}</span>
        <span className="text-xs text-gray-500">· {plan.step_count} step{plan.step_count > 1 ? 's' : ''}</span>
        {plan.is_mock && (
          <span className="text-xs px-1 bg-yellow-100 text-yellow-900 rounded">MOCK</span>
        )}
        {plan.model && !plan.is_mock && (
          <span className="text-xs px-1 bg-green-100 text-green-900 rounded font-mono">{plan.model}</span>
        )}
        <span className="text-xs text-gray-500 ml-auto">
          {new Date(plan.proposed_at).toLocaleString('fr-BE')}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 bg-gray-50">
          {loadingDetail ? (
            <div className="text-sm text-gray-500">Chargement détail…</div>
          ) : detail ? (
            <PlanFullDetail detail={detail} />
          ) : null}
        </div>
      )}
    </article>
  )
}

const PlanFullDetail: React.FC<{ detail: PlanDetail }> = ({ detail }) => {
  const ctx = detail.context_snapshot as Record<string, unknown>
  return (
    <div className="space-y-4 text-sm">
      <section>
        <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-1">Analysis</h4>
        <pre className="whitespace-pre-wrap font-sans text-gray-800 text-sm">{detail.analysis}</pre>
      </section>

      <section>
        <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-2">
          Steps ({detail.steps.length})
        </h4>
        <div className="space-y-2">
          {detail.steps.map((s, i) => (
            <StepCard key={i} step={s} index={i + 1} />
          ))}
        </div>
      </section>

      <section className="text-xs text-gray-600 space-x-3 pt-2 border-t border-gray-200">
        <span>status : <strong>{detail.status}</strong></span>
        {ctx.input_tokens != null && <span>· tokens in : {String(ctx.input_tokens)}</span>}
        {ctx.output_tokens != null && <span>· tokens out : {String(ctx.output_tokens)}</span>}
        {ctx.elapsed_ms != null && <span>· {String(ctx.elapsed_ms)} ms</span>}
        {detail.validated_at && <span>· validé {new Date(detail.validated_at).toLocaleString('fr-BE')}</span>}
        {detail.executed_at && <span>· exécuté {new Date(detail.executed_at).toLocaleString('fr-BE')}</span>}
      </section>
    </div>
  )
}

const StepCard: React.FC<{ step: { type: string; content: Record<string, unknown> }; index: number }> = ({ step, index }) => {
  const c = step.content as Record<string, any>
  const colorClass =
    step.type === 'email' ? 'bg-purple-50 border-purple-200'
    : step.type === 'tracker_com' ? 'bg-amber-50 border-amber-200'
    : step.type === 'sms' ? 'bg-green-50 border-green-200'
    : step.type === 'no_action' ? 'bg-gray-50 border-gray-200'
    : 'bg-white border-gray-200'

  return (
    <div className={`border rounded px-3 py-2 ${colorClass}`}>
      <div className="flex items-baseline gap-2 mb-1 text-xs">
        <span className="font-semibold uppercase">#{index} · {step.type}</span>
        {c.to && <span>→ {c.to}</span>}
        {c.assign_to && <span>→ {c.assign_to}</span>}
        {c.template_key && <span className="font-mono">template={c.template_key} ({c.language})</span>}
      </div>
      {c.subject && <div className="text-sm font-semibold mb-1">{c.subject}</div>}
      {(c.body || c.message || c.reason) && (
        <pre className="text-xs whitespace-pre-wrap font-sans text-gray-800">
          {c.body || c.message || c.reason}
        </pre>
      )}
      {Array.isArray(c.attachments) && c.attachments.length > 0 && (
        <div className="mt-1 text-xs text-gray-600">
          📎 {c.attachments.map((a: any) => a.filename || a.path || JSON.stringify(a)).join(', ')}
        </div>
      )}
    </div>
  )
}
